import { ReactElement, ReactNode, useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { Icon, TIconProps } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { Stack } from '@/common/components/Stack/Stack';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { tv } from '@/common/theme/tv.config';
import { DateUtils, INTL_MONTH_YEAR_FORMAT } from '@/common/utils/date';
import {
  ArrayUtils,
  MoneyUtils,
  TMoney,
} from '@coverflex-tech/hypernova-utils';

export type TTransactionListItem = {
  icon: ReactElement<TIconProps>;
  date: string;
  description: ReactNode;
  statusBadge?: ReactNode;
  amount: TMoney;
  savingsAmount?: TMoney;
  isDebit: boolean;
  disabled?: boolean;
  action?: () => void;
};

export function TransactionsList({
  transactions,
}: {
  transactions: TTransactionListItem[];
}) {
  return (
    <Stack className={styles.root}>
      {transactions.map((transaction, index) => {
        const icon = transaction.icon;
        const Parent = transaction.action ? Pressable : View;
        return (
          <Parent
            key={index}
            onPress={transaction.action}
            className={styles.item({
              pressable: !!transaction.action,
            })}
          >
            <Stack horizontal align="center" className={styles.left} grow>
              {icon && (
                <icon.type
                  {...icon.props}
                  color={transaction.disabled ? 'neutral20' : icon.props.color}
                />
              )}
              <Stack horizontal align="center" grow className={styles.details}>
                <Stack grow>
                  <Label
                    size="sm"
                    color={transaction.disabled ? 'neutral20' : undefined}
                    context="provider"
                  >
                    {transaction.description}
                  </Label>
                  <Label size="sm" color="neutral20">
                    {DateUtils.formatDate(transaction.date)}
                  </Label>
                </Stack>
                <View className={styles.status}>{transaction.statusBadge}</View>
              </Stack>
            </Stack>
            <Stack horizontal align="center" className={styles.right}>
              <Label
                size="sm"
                bold
                color={transaction.isDebit ? undefined : 'systemSuccess'}
                className={styles.amount({
                  disabled: transaction.disabled,
                })}
              >
                {transaction.isDebit ? '-' : '+'}
                {MoneyUtils.moneyToString(transaction.amount)}
              </Label>
              <View className={styles.chevron}>
                {transaction.action && (
                  <Icon icon="chevron-right" color="neutral20" />
                )}
              </View>
            </Stack>
          </Parent>
        );
      })}
    </Stack>
  );
}

export function TransactionsListGroup({
  transactions,
}: {
  transactions: TTransactionListItem[];
}) {
  const { employee } = useEmployeeDetails();
  const groupedItems = useMemo(
    () =>
      ArrayUtils.groupBy(
        ArrayUtils.orderBy<TTransactionListItem>(
          transactions,
          (transaction) => transaction.date,
          'desc'
        ),
        (transaction: TTransactionListItem) =>
          DateUtils.intlFormat(transaction.date, INTL_MONTH_YEAR_FORMAT, {
            language: employee.language,
          })
      ),
    [transactions, employee.language]
  );

  return Object.entries(groupedItems).map(([date, transactions]) => {
    return (
      <Stack key={date} gap="md">
        <Label>{date}</Label>
        <TransactionsList transactions={transactions} />
      </Stack>
    );
  });
}

export type TTransactionsListSkeletonProps = {
  numItems?: number;
  grouped?: boolean;
};

export function TransactionsListSkeleton({
  numItems = 5,
  grouped,
}: TTransactionsListSkeletonProps) {
  const listSkeleton = (
    <Stack className={styles.root}>
      {Array.from({ length: numItems }).map((_, index) => (
        <View key={index} className={styles.item()}>
          <Stack horizontal align="center" className={styles.left} grow>
            <SkeletonInline className={styles.iconSkeleton} />
            <Stack horizontal align="center" grow className={styles.details}>
              <Stack grow gap="xs">
                <SkeletonInline className={styles.descriptionSkeleton} />
                <SkeletonInline className={styles.dateSkeleton} />
              </Stack>
            </Stack>
          </Stack>
          <Stack horizontal align="center" className={styles.right}>
            <SkeletonInline className={styles.amountSkeleton} />
          </Stack>
        </View>
      ))}
    </Stack>
  );

  if (grouped) {
    return (
      <Stack gap="md">
        <SkeletonInline className={styles.amountSkeleton} />
        {listSkeleton}
      </Stack>
    );
  }

  return listSkeleton;
}

const styles = {
  root: 'gap-[0.1rem] rounded-xl overflow-hidden',
  item: tv({
    base: 'flex-row items-center justify-between gap-sm py-md lg:gap-0 lg:bg-neutral10 lg:p-lg',
    variants: {
      pressable: {
        true: 'hover:bg-neutral30',
      },
    },
  }),
  left: 'gap-sm lg:gap-md',
  details: 'flex-col lg:flex-row items-start lg:items-center lg:gap-sm',
  status: 'lg:w-[20rem] items-start pt-xs lg:pt-0',
  right: 'gap-sm lg:gap-md lg:w-[12.8rem] whitespace-nowrap justify-end',
  amount: tv({
    base: '',
    variants: {
      disabled: {
        true: 'text-neutral20 line-through',
      },
    },
  }),
  chevron: 'min-w-[1.6rem]',
  iconSkeleton: 'w-md h-md rounded-full',
  descriptionSkeleton: 'w-[20rem] h-[1.8rem]',
  dateSkeleton: 'w-[10rem] h-[1.8rem]',
  amountSkeleton: 'w-[10rem] h-[2rem]',
};
